import React, { useEffect, useState } from "react";
import { api, formatDate } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Download, RefreshCw, AlertTriangle, CheckCircle2, HelpCircle } from "lucide-react";
import { formatApiError } from "../lib/errors";

const fmtMoney = (n) => n == null ? "—"
  : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n);

const CLASS_META = {
  A: { color: "#065F46", bg: "#D1FAE5", border: "#6EE7B7", label: "Operator-entered", icon: CheckCircle2 },
  B: { color: "#92400E", bg: "#FEF3C7", border: "#FCD34D", label: "System-defaulted", icon: HelpCircle },
  C: { color: "#991B1B", bg: "#FEE2E2", border: "#FCA5A5", label: "Missing / unknown", icon: AlertTriangle },
};

export default function AdminSettlementDiagnostic() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/finance/diagnostic/settlements", { params: { format: "json" }});
      setData(data);
    } catch (e) {
      toast.error("Load failed: " + formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const resp = await api.get("/finance/diagnostic/settlements", {
        params: { format: "csv" }, responseType: "blob",
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `settlement_diagnostic_${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed: " + formatApiError(e));
    } finally { setDownloading(false); }
  };

  if (loading) return <div className="p-10 text-[#6B7280]" data-testid="diagnostic-loading">Running diagnostic…</div>;
  if (!data) return null;

  const s = data.summary; const rows = data.rows || [];
  const dt = s.dollar_totals; const cnt = s.counts;

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="settlement-diagnostic">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="uppercase-label">Finance — pre-flight</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-[#0A2540]">Settlement diagnostic</h1>
          <div className="text-sm text-[#6B7280] mt-1.5">Read-only. Classifies every real settled/closed loan into A / B / C using the same math kernel as production payoff calculations.</div>
          <div className="text-[11px] text-[#9CA3AF] mt-1 tabular-nums">Generated {formatDate(s.generated_at)} · by {s.generated_by || "—"} · tolerance ${s.tolerance?.toFixed(2)}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={load} data-testid="diagnostic-refresh-btn" className="h-9">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button onClick={downloadCsv} disabled={downloading} data-testid="diagnostic-download-csv-btn"
                  className="bg-[#0066FF] hover:bg-[#0052CC] text-white h-9">
            <Download className="w-3.5 h-3.5 mr-1.5" /> {downloading ? "Preparing…" : "Download CSV"}
          </Button>
        </div>
      </div>

      {/* Classification counts — A/B/C portfolio-data provenance scheme */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <ClassCard letter="A" count={cnt.A || 0} total={cnt.total} testid="kpi-class-a" />
        <ClassCard letter="B" count={cnt.B || 0} total={cnt.total} testid="kpi-class-b" />
        <ClassCard letter="C" count={cnt.C || 0} total={cnt.total} testid="kpi-class-c" />
        <div className="rounded-lg border border-[#E5E7EB] bg-white p-4">
          <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Total settled / closed</div>
          <div className="text-3xl font-semibold text-[#0A2540] mt-1.5 tabular-nums" data-testid="kpi-total">{cnt.total}</div>
          <div className="text-[11px] text-[#9CA3AF] mt-2">{s.complex_case_count} flagged COMPLEX_ECONOMICS</div>
        </div>
      </div>

      {/* Dollar totals — components are SEPARATE, no double-counting */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 mb-6">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Dollar totals</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <DollarStat label="Original principal"                value={fmtMoney(dt.original_principal)} />
          <DollarStat label="Additional advances"              value={fmtMoney(dt.additional_advances)} />
          <DollarStat label="Capital deployed"                 value={fmtMoney(dt.capital_deployed)} accent />
          <DollarStat label="Recorded settlement (A + B)" value={fmtMoney(dt.recorded_settlement_ab)} accent
                      hint="Reflects operational settlement workflow. Finance V2 treats this as source of truth; external QB/bank verification is out of scope." />
          <DollarStat label="Principal returned (simple only)" value={fmtMoney(dt.principal_returned_simple_only)}
                      hint="Excludes rows flagged COMPLEX_ECONOMICS" />
          <DollarStat label="Realized funding revenue (simple only)" value={fmtMoney(dt.realized_funding_revenue_simple_only)}
                      hint="Recorded settlement − principal deployed, floored" />
          <DollarStat label="Principal loss (simple only)"     value={fmtMoney(dt.principal_loss_simple_only)}
                      hint="Principal deployed − recorded settlement, where positive" />
          <DollarStat label="Contractual amount forgone (simple only)" value={fmtMoney(dt.contractual_amount_forgone_simple_only)}
                      hint="Contractual payoff at settlement − recorded settlement" />
          <DollarStat label="Negotiated reductions (separate)" value={fmtMoney(dt.negotiated_reductions)} />
          <DollarStat label="Paydowns applied"                 value={fmtMoney(dt.paydowns_applied)} />
          <DollarStat label="Avg recorded settlement (A+B)"    value={fmtMoney(s.averages.recorded_settlement_ab)} />
          <DollarStat label="Avg case duration"                value={`${s.averages.case_duration_days.toFixed(1)} days`}
                      hint="Excludes placeholder settlement dates" />
        </div>
      </div>

      {/* Date reliability */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white p-5 mb-6">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] mb-3">Settlement date reliability</div>
        <div className="grid grid-cols-3 gap-4">
          <DollarStat label="With stored settlement date" value={s.date_reliability.with_settlement_date} />
          <DollarStat label="Placeholder dates flagged"   value={s.date_reliability.placeholder_settlement_dates}
                      hint="Same-day fund+settle OR settlement_date_backfill event" />
          <DollarStat label="Missing settlement date"     value={s.date_reliability.missing_settlement_date} />
        </div>
      </div>

      {/* Per-loan table */}
      <div className="rounded-lg border border-[#E5E7EB] bg-white overflow-hidden">
        <div className="text-[11px] uppercase tracking-wider text-[#6B7280] p-4 border-b border-[#F3F4F6]">Per-loan detail ({rows.length} loans)</div>
        <div className="overflow-auto max-h-[600px]">
          <table className="w-full text-xs" data-testid="diagnostic-table">
            <thead className="bg-[#F9FAFB] sticky top-0">
              <tr className="text-left text-[#6B7280] uppercase tracking-wider">
                <th className="px-3 py-2 font-medium">Loan</th>
                <th className="px-3 py-2 font-medium">Client</th>
                <th className="px-3 py-2 font-medium">Class</th>
                <th className="px-3 py-2 font-medium text-right">Principal</th>
                <th className="px-3 py-2 font-medium text-right">Deployed</th>
                <th className="px-3 py-2 font-medium text-right">Contractual</th>
                <th className="px-3 py-2 font-medium text-right">Recorded</th>
                <th className="px-3 py-2 font-medium text-right">Returned</th>
                <th className="px-3 py-2 font-medium text-right">Revenue</th>
                <th className="px-3 py-2 font-medium text-right">Loss</th>
                <th className="px-3 py-2 font-medium">Funding date</th>
                <th className="px-3 py-2 font-medium">Settlement date</th>
                <th className="px-3 py-2 font-medium">Flags</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.loan_id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]" data-testid={`row-${r.loan_id}`}>
                  <td className="px-3 py-2 font-mono text-[#0A2540]">{r.loan_id}</td>
                  <td className="px-3 py-2 text-[#0A2540]">{r.client_name || "—"}</td>
                  <td className="px-3 py-2"><ClassChip letter={r.classification} /></td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.principal)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(r.principal_deployed)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#6B7280]">{fmtMoney(r.contractual_payoff_at_settle ?? r.contractual_at_settle)}</td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtMoney(r.recorded_settlement)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#065F46]">{fmtMoney(r.principal_returned)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#0066FF]">{fmtMoney(r.realized_funding_revenue)}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#991B1B]">{r.principal_loss > 0 ? fmtMoney(r.principal_loss) : "—"}</td>
                  <td className="px-3 py-2 text-[#6B7280] font-mono">{formatDate(r.funding_date)}</td>
                  <td className="px-3 py-2 text-[#6B7280] font-mono">
                    {formatDate(r.settlement_date)}
                    {r.settlement_date_is_placeholder && <span className="ml-1 text-[9px] text-[#B91C1C]">⚑</span>}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-[#9CA3AF] max-w-[240px]">
                    {(r.flags || []).slice(0, 3).map((f, i) => (
                      <span key={i} className="inline-block bg-[#F3F4F6] rounded px-1.5 py-0.5 mr-1 mb-0.5">{f}</span>
                    ))}
                    {r.flags.length > 3 && <span className="text-[#6B7280]">+{r.flags.length - 3}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-6 rounded-lg border border-[#FEF3C7] bg-[#FFFBEB] p-4">
        <div className="text-[11px] uppercase tracking-wider text-[#92400E] mb-2 font-medium">Methodology notes</div>
        <ul className="text-xs text-[#78350F] space-y-1 list-disc pl-4">
          {s.notes?.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      </div>
    </div>
  );
}

const ClassCard = ({ letter, count, total, testid }) => {
  const m = CLASS_META[letter]; const Icon = m.icon;
  const pct = total ? Math.round(count / total * 100) : 0;
  return (
    <div className="rounded-lg border p-4" style={{ borderColor: m.border, backgroundColor: m.bg }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-4 h-4" style={{ color: m.color }} />
        <span className="text-[11px] uppercase tracking-wider font-medium" style={{ color: m.color }}>Class {letter} · {m.label}</span>
      </div>
      <div className="text-3xl font-semibold tabular-nums" style={{ color: m.color }}>{count}</div>
      <div className="text-[11px] mt-1" style={{ color: m.color, opacity: 0.7 }}>{pct}% of settled book</div>
    </div>
  );
};

const ClassChip = ({ letter }) => {
  const m = CLASS_META[letter];
  return <span className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded"
               style={{ color: m.color, backgroundColor: m.bg, border: `1px solid ${m.border}` }}
               data-testid={`class-chip-${letter}`}>{letter}</span>;
};

const DollarStat = ({ label, value, accent, hint }) => (
  <div>
    <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">{label}</div>
    <div className={`text-lg font-medium tabular-nums mt-1 ${accent ? "text-[#0066FF]" : "text-[#0A2540]"}`}>{value}</div>
    {hint && <div className="text-[10px] text-[#9CA3AF] mt-0.5">{hint}</div>}
  </div>
);
